import { AssistantConfig } from '@/shared/types/space'
import { BadgeType } from '@/ui/components/Badge/types'
import React from 'react'

interface Route {
    id?: string
    children?: Route[]
}

interface Assistant {
    id?: string
    config: {
        id?: string
        name?: string
        badges?: BadgeType[]
    }
    assistants?: Assistant[]
    appearance?: {
        shortDescription?: string
    }
}

interface User {
    profilePhoto?: string | null
}

class Utils {
    addListEntryToMap<K, V>(map: Map<K, V[]>, key: K, value: V): void {
        if (!map.has(key)) {
            map.set(key, [])
        }

        map.get(key)!.push(value)
    }

    delay = (ms: number): Promise<void> => {
        return new Promise((resolve) => setTimeout(resolve, ms))
    }

    flattenMap = <K, V,>(map: Map<K, V[]>): V[] => {
        const flattenedList: V[] = []

        for (const [, value] of map) {
            flattenedList.push(...value)
        }

        return flattenedList
    }

    remToPixel = (rem: number): number => {
        const rootFontSize = parseFloat(
            getComputedStyle(document.documentElement).fontSize
        )
        const pixelValue = rem * rootFontSize

        return pixelValue
    }

    filterObjectArrayByParameters = <T extends object>(
        list: T[],
        parameters: (keyof T)[]
    ): Partial<T>[] => {
        const newlist = list.map((item) => {
            const newItem: Partial<T> = {}
            for (const p of parameters) {
                newItem[p] = item[p]
            }
            return newItem
        })
        return newlist
    }

    isFormData = (source: unknown): source is FormData => {
        return source instanceof FormData
    }

    capitalizeFirstLetter = (string: string): string => {
        return string.charAt(0).toUpperCase() + string.slice(1)
    }

    truncateString = (string: string, startIndex: number, endIndex: number): string =>
        string.substring(startIndex, endIndex)

    truncateStringWithEllipsis = (str: string, startIndex: number, endIndex: number): string => {
        return str.length > endIndex
            ? `${this.truncateString(str, startIndex, endIndex)}...`
            : str
    }

    isAccessTokenExpired = (token: string): boolean => {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const expiry = payload.exp * 1000

        return Date.now() > expiry
    }

    /**
     * Checks if the given id already exists and if so, returns an incremented version.
     * NOTE: This is because route id's must be unique.
     */
    integrateId = (
        id: string,
        list: Route[],
        increment = 0,
        integratedId?: string,
        originList?: Route[]
    ): string => {
        if (!integratedId) integratedId = id

        if (!originList) originList = list

        for (const entry of list) {
            if (entry.id === integratedId) {
                integratedId = `${id}-${++increment}`
                integratedId = this.integrateId(id, originList, increment, integratedId, originList)
            } else if (entry.children) {
                integratedId = this.integrateId(id, entry.children, increment, integratedId, originList)
            }
        }

        return integratedId
    }

    // Recursively finds and returns a route with the given id, or undefined if not found
    getRouteById = (id: string, list: Route[]): Route | undefined => {
        for (const route of list) {
            if (route.id === id) return route
            if (route.children) {
                const found = this.getRouteById(id, route.children)
                if (found) return found
            }
        }
        return undefined
    }

    formatEndpoint = (endpoint: string, params: Record<string, unknown> = {}): string => {
        let formattedEndpoint = endpoint

        for (const key in params) {
            formattedEndpoint = formattedEndpoint.replace(`{${key}}`, String(params[key]))
        }

        return formattedEndpoint
    }

    addRouteById = (id: string, list: Route[], newEntry: Route | Route[]): Route | undefined => {
        const route = this.getRouteById(id, list)
        if (!route) return undefined

        route.children = route.children || []
        const children = route.children

        const pushIfNotExists = (candidate: Route): boolean => {
            const exists = children.some((child) => child.id === candidate.id)
            if (!exists) children.push(candidate)
            return exists
        }

        if (Array.isArray(newEntry)) {
            for (const newRoute of newEntry) {
                pushIfNotExists(newRoute)
            }
        } else {
            const already = pushIfNotExists(newEntry)
            if (already) console.warn(`Route with id ${newEntry.id} already exists in route with id ${id}`)
        }

        return route
    }

    compareArrays = <T,>(source: T[], target: T[]): boolean => {
        const areDifferent = source.length === target.length && source.every((value, index) => value === target[index])

        return areDifferent
    }

    getParentId = (childId: string, list: Assistant[], currentParentId?: string): string | undefined => {
        let parentId: string | undefined = undefined

        for (const entry of list) {
            const assistants = entry.assistants

            if (entry?.config?.id === childId) {
                parentId = currentParentId
                break
            }

            if (assistants) {
                parentId = this.getParentId(childId, assistants, entry.id ?? entry.config?.id)

                if (parentId) break
            }
        }

        return parentId
    }

    checkExpiration = (expirationDate: number, expirationThreshold: number): boolean => {
        const now = Date.now()

        let isExpired = true

        if (expirationDate)
            isExpired = expirationDate - now < expirationThreshold

        return isExpired
    }

    checkExpirationMilliseconds = (expirationDate: number, expirationThreshold: number): boolean => {
        const now = Math.floor(Date.now() / 1000) // current time in seconds
        const thresholdInSeconds = Math.floor(expirationThreshold / 1000) // convert milliseconds to seconds

        let isExpired = true

        if (expirationDate)
            isExpired = expirationDate - now < thresholdInSeconds

        return isExpired
    }

    sortByVersion = <T extends { version: string }>(list: T[]): T[] => {
        const sortedList = [...list]

        sortedList.sort((a, b) => {
            return b.version.localeCompare(a.version, undefined, { numeric: true })
        })

        return sortedList
    }

    checkDefaultDescription = (
        description: string | undefined,
        appearance?: { shortDescription?: string }
    ): string | undefined => {
        return !description || description === "description"
            ? appearance?.shortDescription
            : description
    }

    checkDefaultName = (
        name: string | undefined,
        assistant?: Assistant
    ): string | undefined => {
        return !name || name === "name"
            ? assistant?.config.name
            : name
    }

    formatTimePeriod = (
        timestamp: string,
        locale: string | undefined,
        translate: (key: string) => string
    ): string => {
        const now = new Date()
        const date = new Date(timestamp)
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000)

        const getShortDayName = (date: Date): string => {
            return new Intl.DateTimeFormat(locale ?? 'en', { weekday: 'short' }).format(date)
        }

        if (diffInMinutes < 1) {
            return translate('general:time:justNow')
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} ${translate('general:time:minAgo')}`
        } else {
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const yesterday = new Date(today)
            yesterday.setDate(today.getDate() - 1)

            if (date >= today) {
                return translate('general:time:today')
            } else if (date >= yesterday) {
                return translate('general:time:yesterday')
            } else {
                return `${getShortDayName(date)} ${date.toLocaleDateString()}`
            }
        }
    }

    abbreviateLabel = (label: string, substring: number): string => {
        return `${label.substring(0, substring)} ...`
    }

    getProfilePicture = (user: User): React.ReactElement => {
        let picture: React.ReactElement = (
            <i className="a-icon boschicon-bosch-ic-my-brand-frame"></i>
        )

        if (user?.profilePhoto) {
            picture = (
                <img
                    className="profile-picture"
                    src={user?.profilePhoto}
                    alt="Profile"
                />
            )
        }

        return picture
    }

    getAssistantBadges(arg: Assistant | AssistantConfig | undefined): BadgeType[] {
        if (!arg) return []

        if ('config' in arg) {
            return arg.config.badges || []
        }
        return arg.badges || []
    }

    /**
     * Extracts the user's first name from MSAL account name string.
     * Handles the format "Surname Firstname (Department)" by extracting the word immediately before the parenthesis.
     * Falls back to the original name if no parenthesis is found.
     *
     * @param name - The full name string from MSAL account (e.g., "Doe John (BMC/UXC)")
     * @returns The extracted first name (e.g., "John"), or the original name if parsing fails, or undefined if input is undefined
     *
     * @example
     * extractFirstName("Doe John (BMC/UXC)") // returns "John"
     * extractFirstName("John") // returns "John"
     * extractFirstName(undefined) // returns undefined
     */
    extractFirstName = (name?: string): string | undefined => {
        if (!name) return name

        const match = name.match(/(\S+)\s*\(/)
        return match?.[1] || name
    }
}

export default new Utils()
